import React from 'react';
import { format } from 'date-fns';
import { AlertTriangle, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import type { Coach } from '@court-sync/shared';
import { formatTimeForTooltip, formatTimeHHMM } from '@/utils/timeFormat';
import {
  lessonCalendarOccurrenceToClickPayload,
  type LessonOccurrenceClickPayload,
} from '@/utils/scheduleOccurrences';
import type { LessonOccurrence } from './LessonCalendarView';
import { LessonOccurrenceDayPopover } from './LessonOccurrenceDayPopover';
import type { LessonAvailabilityWarningDays } from '@/utils/coachAvailabilityWarnings';
import {
  getLessonOccurrenceMetaLabelParts,
  type LessonCalendarTooltipContext,
} from '@/utils/lessonCalendarTooltip';

export interface LessonCalendarDayPopoverContentProps {
  day: Date;
  occurrences: LessonOccurrence[];
  getDotChipProps: (occ: LessonOccurrence) => { className: string; style?: React.CSSProperties };
  onLessonOccurrenceClick?: (payload: LessonOccurrenceClickPayload) => void;
  onScheduleOccurrenceClick?: (occurrence: LessonOccurrence) => void;
  onClose?: () => void;
  coaches?: Coach[];
  coachAvailabilityWarnings?: LessonAvailabilityWarningDays;
  hasCoach: (lesson: LessonOccurrence['lesson']) => boolean;
  tooltipContext?: LessonCalendarTooltipContext;
  /** Tailwind max-height classes for the scrollable lesson list */
  listMaxHeightClassName?: string;
}

function occurrenceTimeLabel(occ: LessonOccurrence): string {
  const startRaw = occ.startTime ?? occ.lesson?.startTime ?? '09:00';
  const endRaw = occ.endTime ?? occ.lesson?.endTime ?? '10:00';
  const start = formatTimeHHMM(String(startRaw));
  const end = formatTimeHHMM(String(endRaw));
  const startLabel = formatTimeForTooltip(start);
  const endLabel = formatTimeForTooltip(end);
  if (startLabel && endLabel) {
    return `${startLabel} – ${endLabel}`;
  }
  return startLabel || '';
}

export function LessonCalendarDayPopoverContent({
  day,
  occurrences,
  getDotChipProps,
  onLessonOccurrenceClick,
  onScheduleOccurrenceClick,
  onClose,
  coaches = [],
  coachAvailabilityWarnings = new Map(),
  hasCoach,
  tooltipContext,
  listMaxHeightClassName = 'max-h-[min(16rem,50vh)]',
}: LessonCalendarDayPopoverContentProps) {
  return (
    <>
      <div className="relative border-b border-border px-4 pb-3 pt-4 sm:px-4">
        {onClose ? (
          <button
            type="button"
            className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-800 sm:h-7 sm:w-7"
            aria-label="Close"
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
        <div className={`flex flex-col items-center gap-1.5 ${onClose ? 'pr-8' : ''}`}>
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {format(day, 'EEE')}
          </span>
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-800 text-base font-semibold text-white tabular-nums sm:h-9 sm:w-9 sm:text-sm">
            {format(day, 'd')}
          </span>
        </div>
      </div>

      {occurrences.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          No lessons scheduled
        </p>
      ) : (
        <ul
          className={`${listMaxHeightClassName} space-y-1 overflow-y-auto p-2 sm:p-2 [scrollbar-gutter:stable]`}
        >
          {occurrences.map((occ, idx) => {
            const timeLabel = occurrenceTimeLabel(occ);
            const metaLabel = getLessonOccurrenceMetaLabelParts(occ.lesson, tooltipContext).join(
              ' · '
            );
            const hasWarning =
              coachAvailabilityWarnings.get(occ.lesson.id)?.has(occ.date.getDay()) ?? false;

            const opensDetail =
              (occ.sourceType === 'lesson' && !!onLessonOccurrenceClick) ||
              (occ.sourceType !== 'lesson' && !!onScheduleOccurrenceClick);

            const rowButton = (
              <button
                type="button"
                className={`flex w-full items-start gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-800 sm:py-1.5 ${
                  occ.status === 'cancelled' ? 'opacity-60 line-through' : ''
                }`}
                onClick={
                  opensDetail
                    ? () => {
                        onClose?.();
                        if (occ.sourceType === 'lesson' && onLessonOccurrenceClick) {
                          onLessonOccurrenceClick(lessonCalendarOccurrenceToClickPayload(occ));
                        } else if (onScheduleOccurrenceClick) {
                          onScheduleOccurrenceClick(occ);
                        }
                      }
                    : undefined
                }
              >
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${getDotChipProps(occ).className}`}
                  style={getDotChipProps(occ).style}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  {timeLabel && (
                    <span className="block text-xs text-muted-foreground tabular-nums">
                      {timeLabel}
                    </span>
                  )}
                  <span className="flex min-w-0 items-center gap-1 font-medium leading-snug">
                    {hasWarning && (
                      <AlertTriangle
                        className="h-3.5 w-3.5 shrink-0 text-amber-500"
                        aria-hidden
                      />
                    )}
                    {occ.makeupCount && occ.makeupCount > 0 && (
                      <span className="inline-flex h-4 min-w-[1rem] shrink-0 items-center justify-center rounded-sm bg-fuchsia-600 px-0.5 text-[9px] font-bold text-white dark:bg-fuchsia-500">
                        M
                      </span>
                    )}
                    <span className="truncate">{occ.lesson.name}</span>
                  </span>
                  {metaLabel && (
                    <span className="block truncate text-xs font-normal text-muted-foreground">
                      {metaLabel}
                    </span>
                  )}
                </span>
              </button>
            );

            if (opensDetail) {
              return (
                <li key={`${occ.sourceType}-${occ.lesson.id}-${idx}`}>{rowButton}</li>
              );
            }

            return (
              <li key={`${occ.sourceType}-${occ.lesson.id}-${idx}`}>
                <Popover>
                  <PopoverTrigger asChild>{rowButton}</PopoverTrigger>
                  <PopoverContent className="w-80" align="start" side="right">
                    <LessonOccurrenceDayPopover
                      occurrence={occ}
                      hasCoach={hasCoach(occ.lesson)}
                      coaches={coaches}
                      hasCoachAvailabilityWarning={hasWarning}
                    />
                  </PopoverContent>
                </Popover>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
